import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { mediaUrl } from "@/lib/api";
import Reveal from "@/components/shared/Reveal";
import Seo from "@/components/shared/Seo";
import SpeakerAvatar from "@/components/shared/SpeakerAvatar";
import { ArrowLeft, ArrowRight } from "lucide-react";

const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const frDate = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || "");
  return m ? `${parseInt(m[3], 10)} ${MONTHS_FR[parseInt(m[2], 10) - 1]} ${m[1]}` : (s || "");
};

const ArticleDetail = () => {
  const { slug } = useParams();
  const { data: a, isLoading } = useQuery({ queryKey: ["article", slug], queryFn: async () => (await api.get(`/articles/${slug}`)).data });

  if (isLoading) return <div className="min-h-screen bg-white" />;
  if (!a) return <div className="min-h-screen flex items-center justify-center pt-32 text-f2b-gray">Article introuvable.</div>;

  return (
    <>
      <Seo title={a.seo_title || `${a.title} — FACADES2build`} description={a.meta_description || a.chapo} image={mediaUrl(a.image)} />
      <article>
        <section className={`relative flex items-end bg-f2b-black ${mediaUrl(a.image) ? "min-h-[55vh]" : ""}`}>
          {mediaUrl(a.image) && <img src={mediaUrl(a.image)} alt={a.title} className="absolute inset-0 w-full h-full object-cover opacity-45" />}
          <div className="absolute inset-0 bg-gradient-to-t from-f2b-black via-f2b-black/70 to-transparent" />
          <div className={`f2b-container relative z-10 ${mediaUrl(a.image) ? "pb-16 pt-40" : "pb-12 pt-32"}`}>
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/60 text-sm hover:text-f2b-cyan mb-6"><ArrowLeft size={16} /> Toutes les expertises</Link>
            <Reveal>
              <div className="flex items-center gap-3 text-xs mb-4">
                <span className="text-f2b-cyan uppercase tracking-wider">{a.theme}</span>
                <span className="text-white/60">{frDate(a.date)}</span>
              </div>
              <h1 className="font-display font-bold text-white text-3xl md:text-5xl leading-tight max-w-4xl">{a.title}</h1>
            </Reveal>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="f2b-container grid lg:grid-cols-[1fr_300px] gap-14">
            <div className="max-w-2xl">
              <Reveal><p className="f2b-lead text-f2b-black text-lg md:text-2xl leading-relaxed border-l-2 border-f2b-cyan pl-4 md:pl-6">{a.chapo}</p></Reveal>
              <Reveal delay={0.1}><div className="prose mt-10 text-f2b-text/85 leading-relaxed text-lg whitespace-pre-line">{a.content}</div></Reveal>
              {a.gallery?.length > 0 && (
                <Reveal delay={0.16} className="mt-12 grid sm:grid-cols-2 gap-4">
                  {a.gallery.map((g, i) => (
                    <img key={i} src={mediaUrl(g)} alt={`${a.title} — ${i + 1}`} className="w-full aspect-[4/3] object-cover" />
                  ))}
                </Reveal>
              )}
              {a.replay_url && (
                <Reveal delay={0.18} className="mt-10">
                  <a href={a.replay_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-f2b-black text-white px-6 py-3 text-sm hover:bg-black">▶ Voir le replay</a>
                </Reveal>
              )}
              {a.key_points?.length > 0 && (
                <Reveal delay={0.15} className="mt-10 md:mt-12 bg-f2b-light p-5 md:p-8">
                  <h3 className="f2b-kicker text-f2b-gray mb-3 md:mb-4">Points clés</h3>
                  <ul className="space-y-2 md:space-y-3">
                    {a.key_points.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 md:gap-3 text-f2b-text text-sm md:text-base"><span className="text-f2b-cyan mt-0.5 md:mt-1">—</span>{p}</li>
                    ))}
                  </ul>
                </Reveal>
              )}
              {a.buildinglab_url && (
                <Reveal delay={0.2} className="mt-10 md:mt-12 border border-f2b-filet bg-white p-6 md:p-8" data-testid="buildinglab-block">
                  <p className="f2b-kicker text-f2b-cyan mb-2">Pour aller plus loin sur BuildingLab</p>
                  <p className="text-f2b-text/85 text-sm md:text-base leading-relaxed mb-5">
                    {a.buildinglab_teaser || "Retrouvez l'analyse technique complète de ce sujet — contexte détaillé, choix de conception et enseignements — dans l'article approfondi publié sur BuildingLab."}
                  </p>
                  <a href={a.buildinglab_url} target="_blank" rel="noreferrer" data-testid="buildinglab-cta"
                    className="inline-flex items-center gap-2 bg-f2b-black text-white px-6 py-3 text-sm font-medium hover:bg-black transition-colors">
                    Découvrir le contenu sur BuildingLab <ArrowRight size={16} />
                  </a>
                </Reveal>
              )}
            </div>
            <aside className="space-y-8">
              {a.company && (
                <div className="border border-f2b-filet p-6">
                  <h3 className="f2b-kicker text-f2b-gray mb-2">Société</h3>
                  <p className="font-display font-bold text-f2b-black text-lg">{a.company}</p>
                </div>
              )}
              {a.speaker && (
                <Link to={`/intervenants/${a.speaker.slug}`} className="block border border-f2b-filet p-6 hover:border-f2b-cyan transition-colors">
                  <h3 className="f2b-kicker text-f2b-gray mb-3">Intervenant</h3>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 overflow-hidden shrink-0"><SpeakerAvatar speaker={a.speaker} /></div>
                    <div><p className="font-display font-bold text-f2b-black">{a.speaker.name}</p><p className="text-f2b-gray text-xs">{a.speaker.company}</p></div>
                  </div>
                </Link>
              )}
              {a.author && (
                <div className="border border-f2b-filet p-6">
                  <h3 className="f2b-kicker text-f2b-gray mb-2">Auteur / source</h3>
                  <p className="text-f2b-text">{a.author}</p>
                </div>
              )}
            </aside>
          </div>
          {a.related?.length > 0 && (
            <div className="f2b-container mt-16 pt-10 border-t border-f2b-filet">
              <h2 className="font-display font-bold text-f2b-black text-2xl mb-6">À lire aussi</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {a.related.map((r) => (
                  <Link key={r.id} to={`/blog/${r.slug}`} className="group block" data-testid={`related-${r.id}`}>
                    <div className="aspect-[3/2] overflow-hidden mb-3 bg-f2b-light">
                      {mediaUrl(r.image) && <img src={mediaUrl(r.image)} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    </div>
                    <span className="f2b-kicker text-f2b-cyan">{r.theme}</span>
                    <h3 className="font-display font-bold text-f2b-black text-lg mt-1 leading-tight group-hover:text-f2b-cyan transition-colors">{r.title}</h3>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      </article>
    </>
  );
};

export default ArticleDetail;
